import type { ReploProject, UserWorkspace } from "schemas/generated/project";

import React, { useMemo, useState } from "react";

import { Drawer } from "@replohq/lattice/components/drawer/Drawer";
import {
  PopoverRoot,
  PopoverTrigger,
} from "@replohq/lattice/components/popover/Popover";
import { useIsMobile } from "@replohq/lattice/hooks/use-mobile";
import { cn } from "@replohq/lattice/utils/cn";
import { skipToken } from "@tanstack/react-query";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { AddWorkspaceModal } from "@v2/features/dashboard/sidebar/project-selector/AddWorkspaceModal";
import {
  AgentProjectSelectorHeader,
  AgentProjectSelectorHeaderSkeleton,
} from "@v2/features/dashboard/sidebar/project-selector/AgentProjectSelectorHeader";
import ProjectSelectorMenuContent from "@v2/features/dashboard/sidebar/project-selector/ProjectSelectorMenuContent";
import {
  SidebarProjectSelectorHeader,
  SidebarProjectSelectorHeaderSkeleton,
} from "@v2/features/dashboard/sidebar/project-selector/SidebarProjectSelectorHeader";
import { CreateShopModal } from "@v2/features/new-shop-onboarding/CreateShopModal";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { projectRouter } from "@v2/trpc/routes/project";

export type ProjectSelectorVariant = "sidebar" | "agent-browser";

function ProjectSelectorHeader({
  variant,
  isLoading,
  currentProject,
  currentWorkspace,
  onPersonalize,
}: {
  variant: ProjectSelectorVariant;
  isLoading: boolean;
  currentProject?: ReploProject;
  currentWorkspace?: UserWorkspace;
  onPersonalize?: () => void;
}) {
  const showSkeleton = isLoading || !currentWorkspace;

  if (variant === "sidebar") {
    return showSkeleton ? (
      <SidebarProjectSelectorHeaderSkeleton />
    ) : (
      <SidebarProjectSelectorHeader
        currentProject={currentProject}
        workspaceName={currentWorkspace.workspaceName}
      />
    );
  }

  return showSkeleton ? (
    <AgentProjectSelectorHeaderSkeleton />
  ) : (
    <AgentProjectSelectorHeader
      currentProject={currentProject}
      workspaceName={currentWorkspace.workspaceName}
      onPersonalize={onPersonalize}
    />
  );
}

export function ProjectSelector({
  variant,
  onPersonalize,
}: {
  variant: ProjectSelectorVariant;
  onPersonalize?: () => void;
}) {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateShopModalOpen, setIsCreateShopModalOpen] = useState(false);
  const [isAddWorkspaceModalOpen, setIsAddWorkspaceModalOpen] = useState(false);
  const [createShopWorkspaceId, setCreateShopWorkspaceId] = useState<
    string | undefined
  >();

  const workspaceId = useCurrentWorkspaceId();
  const { project: currentProject } = useCurrentProjectContext();
  const { user } = useCurrentUser();

  // NOTE (Ben, 2026-02-24): We query listGroupedByWorkspaceForUser here (auth: user)
  // instead of relying on useCurrentWorkspace() (auth: workspace) because project-only
  // members don't have workspace membership and the workspace query would
  // return FORBIDDEN.
  const { data: workspaces, isPending: isWorkspacesLoading } =
    projectRouter.listGroupedByWorkspaceForUser.useQuery(
      user?.id
        ? {
            userId: user.id,
            currentWorkspaceId: workspaceId ?? undefined,
          }
        : skipToken,
    );

  const currentWorkspace = useMemo(() => {
    return workspaces?.find(
      (workspace) => workspace.workspaceId === workspaceId,
    );
  }, [workspaces, workspaceId]);

  const createShopTargetWorkspace = useMemo(() => {
    const targetId = createShopWorkspaceId ?? workspaceId;
    return workspaces?.find((workspace) => workspace.workspaceId === targetId);
  }, [workspaces, createShopWorkspaceId, workspaceId]);

  const isLoading = isWorkspacesLoading || !workspaceId;

  if (!currentWorkspace && !isLoading) {
    return null;
  }

  const header = (
    <ProjectSelectorHeader
      variant={variant}
      isLoading={isLoading}
      currentProject={currentProject}
      currentWorkspace={currentWorkspace}
      onPersonalize={onPersonalize}
    />
  );

  const menuContent = workspaceId ? (
    <ProjectSelectorMenuContent
      variant={variant}
      isMobile={isMobile}
      currentProject={currentProject}
      currentWorkspaceId={workspaceId}
      workspaces={workspaces}
      setIsCreateShopModalOpen={setIsCreateShopModalOpen}
      setCreateShopWorkspaceId={setCreateShopWorkspaceId}
      setIsAddWorkspaceModalOpen={setIsAddWorkspaceModalOpen}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  ) : null;

  return (
    <>
      {isMobile ? (
        <Drawer
          open={isOpen}
          onOpenChange={setIsOpen}
          title="Projects"
          trigger={
            <button
              type="button"
              disabled={isLoading}
              data-testid="select-workspace"
              className={cn("text-left", variant === "sidebar" && "w-full")}
            >
              {header}
            </button>
          }
        >
          {menuContent}
        </Drawer>
      ) : (
        <PopoverRoot open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger
            disabled={isLoading}
            UNSAFE_className={cn(variant === "sidebar" && "w-full")}
            data-testid="select-workspace"
          >
            {header}
          </PopoverTrigger>
          {menuContent}
        </PopoverRoot>
      )}

      <AddWorkspaceModal
        open={isAddWorkspaceModalOpen}
        onOpenChange={setIsAddWorkspaceModalOpen}
      />
      <CreateShopModal
        open={isCreateShopModalOpen}
        onOpenChange={setIsCreateShopModalOpen}
        workspaceId={createShopWorkspaceId ?? workspaceId ?? undefined}
        isShopifyPaymentProcessor={
          createShopTargetWorkspace
            ? createShopTargetWorkspace.paymentProcessor === "shopify"
            : undefined
        }
      />
    </>
  );
}
